import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { masterChartPath } from 'constants/masterChartRoutes';
import { useForm, Controller } from 'react-hook-form';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Chip,
  TextField,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import {
  ArrowBackRounded,
  AddRounded,
  EditRounded,
  DeleteRounded,
  HealingRounded,
} from '@mui/icons-material';
import { AuthContext } from 'context/Auth';
import { db } from 'services/firebase';
import { contentCardSx } from 'styles/standaloneLayoutTokens';
import ConfirmDialog from 'components/common/ConfirmDialog';
import DashboardPageHeader from 'components/common/DashboardPageHeader';
import ProvenanceInfoButton from 'components/individual/health-records/ProvenanceInfoButton';

const COMMON_PROCEDURES = [
  'Appendectomy', 'Cholecystectomy', 'Cesarean Section', 'Hysterectomy',
  'Knee Replacement', 'Hip Replacement', 'Coronary Artery Bypass (CABG)',
  'Hernia Repair', 'Tonsillectomy', 'Cataract Surgery', 'Colonoscopy',
  'Endoscopy', 'Laparoscopy', 'Spinal Fusion', 'Mastectomy',
];

const SurgicalHistoryPage = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const [surgeries, setSurgeries] = useState([]);
  const [fullHealthRecords, setFullHealthRecords] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(-1);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const { control, handleSubmit, reset, setValue } = useForm({
    defaultValues: { procedure: '', year: '', hospital: '', notes: '' },
  });

  useEffect(() => {
    if (!user?.uid) return;
    const load = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const doc = await db.collection('users').doc(user.uid).get();
        const hr = doc.data()?.healthRecords || {};
        setFullHealthRecords(hr);
        setSurgeries(hr.psh || []);
      } catch {
        setLoadError(
          'Unable to load surgical history — retry before assuming you have none.',
        );
        setSurgeries([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user?.uid]);

  const saveToFirestore = useCallback(
    async (updated) => {
      const merged = {
        ...(fullHealthRecords ?? {}),
        psh: updated,
        updatedAt: new Date().toISOString(),
      };
      await db
        .collection('users')
        .doc(user.uid)
        .set({ healthRecords: merged }, { merge: true });
      setFullHealthRecords(merged);
    },
    [user?.uid, fullHealthRecords]
  );

  const openDialog = (index = -1) => {
    setEditingIndex(index);
    if (index >= 0) {
      const s = surgeries[index];
      reset({
        procedure: s.procedure ?? '',
        year: s.year ?? '',
        hospital: s.hospital ?? '',
        notes: s.notes ?? '',
      });
    } else {
      reset({ procedure: '', year: '', hospital: '', notes: '' });
    }
    setDialogOpen(true);
  };

  const onDialogSubmit = handleSubmit(async (data) => {
    setSaving(true);
    setError(null);
    try {
      const toSave = {
        procedure: data.procedure.trim(),
        year: data.year ? String(data.year).trim() : '',
        hospital: data.hospital.trim(),
        notes: data.notes.trim(),
      };
      const updated = [...surgeries];
      if (editingIndex >= 0) {
        updated[editingIndex] = toSave;
      } else {
        updated.push(toSave);
      }
      updated.sort((a, b) => (parseInt(b.year) || 0) - (parseInt(a.year) || 0));
      await saveToFirestore(updated);
      setSurgeries(updated);
      setDialogOpen(false);
      setSuccessMsg(editingIndex >= 0 ? 'Entry updated.' : 'Surgery added.');
    } catch {
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  });

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const updated = surgeries.filter((_, i) => i !== pendingDelete.index);
      setSurgeries(updated);
      await saveToFirestore(updated);
      setSuccessMsg(`Removed "${pendingDelete.label}".`);
      setPendingDelete(null);
    } catch {
      setError('Failed to remove. Please try again.');
      setPendingDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="inner">
      <Box sx={{ mt: { xs: 1, sm: 5 }, mb: 1 }}>
        <Button
          startIcon={<ArrowBackRounded />}
          onClick={() => navigate(masterChartPath())}
          size="small"
        >
          Back to Health Records
        </Button>
      </Box>
      <DashboardPageHeader
        title="Surgical History"
        subtitle="Previous surgeries and procedures"
        sx={{ mt: 0 }}
        actions={
          <Button
            variant="contained"
            startIcon={<AddRounded />}
            onClick={() => openDialog()}
          >
            Add Surgery
          </Button>
        }
      />

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {successMsg && (
        <Alert severity="success" onClose={() => setSuccessMsg(null)} sx={{ mb: 2 }}>
          {successMsg}
        </Alert>
      )}

      {loadError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {loadError}
        </Alert>
      )}

      <Paper elevation={0} sx={{ ...contentCardSx, mb: 2 }}>
        {loading ? (
          <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : loadError ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="error" gutterBottom>
              Surgical history unavailable until the load succeeds.
            </Typography>
          </Box>
        ) : surgeries.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <HealingRounded sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
            <Typography color="text.secondary" gutterBottom>
              No surgical history added yet.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Add your previous surgeries and procedures.
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Procedure</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Year</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Hospital</TableCell>
                  <TableCell sx={{ width: 88 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {surgeries.map((s, i) => (
                  <TableRow key={i} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" fontWeight={600}>{s.procedure}</Typography>
                        <ProvenanceInfoButton source={s.source} />
                      </Box>
                      {s.notes && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {s.notes}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{s.year || ''}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">{s.hospital || ''}</Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <IconButton
                        size="small"
                        onClick={() => openDialog(i)}
                        aria-label={`Edit procedure: ${s.procedure}`}
                      >
                        <EditRounded fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setPendingDelete({ index: i, label: s.procedure })}
                        aria-label={`Delete procedure: ${s.procedure}`}
                      >
                        <DeleteRounded fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingIndex >= 0 ? 'Edit Entry' : 'Add Surgery / Procedure'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Controller
              name="procedure"
              control={control}
              rules={{ required: 'Procedure name is required' }}
              render={({ field, fieldState: { error: fieldError } }) => (
                <TextField
                  {...field}
                  label="Procedure"
                  fullWidth
                  placeholder="e.g., Appendectomy"
                  error={!!fieldError}
                  helperText={fieldError?.message}
                />
              )}
            />

            <Controller
              name="year"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Year"
                  type="number"
                  fullWidth
                  placeholder={String(new Date().getFullYear())}
                  inputProps={{ min: 1900, max: new Date().getFullYear() }}
                />
              )}
            />

            <Controller
              name="hospital"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Hospital / Facility"
                  fullWidth
                  placeholder="Where was the procedure performed?"
                />
              )}
            />

            <Controller
              name="notes"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Notes"
                  fullWidth
                  multiline
                  rows={2}
                  placeholder="Complications, outcomes, or other details (optional)"
                />
              )}
            />

            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                gutterBottom
              >
                Common procedures:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {COMMON_PROCEDURES.slice(0, 8).map((name) => (
                  <Chip
                    key={name}
                    label={name}
                    size="small"
                    variant="outlined"
                    onClick={() => setValue('procedure', name)}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <LoadingButton
            variant="contained"
            onClick={onDialogSubmit}
            loading={saving}
            disabled={saving}
          >
            {editingIndex >= 0 ? 'Update' : 'Add'}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        title="Remove Entry"
        message={`Remove "${pendingDelete?.label}"?`}
        confirmLabel="Remove"
        confirmColor="error"
        loading={deleting}
        onConfirm={handleDelete}
        onExited={() => setPendingDelete(null)}
      />
    </div>
  );
};

export default SurgicalHistoryPage;
